import { useState } from 'react';
import { ExclamationTriangle } from 'react-bootstrap-icons';

import { ROUTES } from '@repo/common';
import { BillingPlanDisputeState, ContactTopic, DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { ContactSupportDialog } from '@repo/ui/components/contact-support/contact-support-dialog';

const DISPUTE_COPY: Record<BillingPlanDisputeState, { title: string; body: string }> = {
  [BillingPlanDisputeState.UNDER_REVIEW]: {
    title: 'A payment for this plan is disputed',
    body: 'Your bank is reviewing a disputed payment, so your access is paused until it decides. If you opened the dispute by mistake, ask your bank to withdraw it, or contact us and we will help.',
  },
  [BillingPlanDisputeState.INQUIRY]: {
    title: 'Your bank asked about a payment',
    body: 'Your bank has questions about a payment for this plan. Your access continues while they are answered. Contact us if anything about the charge looks wrong.',
  },
};

/**
 * Tells a member their bank is disputing a payment, and what that means for
 * their access, which otherwise they would only notice as a locked course.
 *
 * Withdrawing a dispute is between the member and their bank, so the page
 * cannot do it for them; it can say so and put support one click away.
 */
export function DisputeNotice({ dispute, productTitle }: { dispute: BillingPlanDisputeState; productTitle: string }) {
  const [isContactingSupport, setIsContactingSupport] = useState(false);
  const copy = DISPUTE_COPY[dispute];

  return (
    <SDSFlexbox
      className="DisputeNotice"
      direction="row"
      gap="text-large"
      alignItems="flex-start"
      fullWidth
      dataTestId={DataTestIds.BILLING.DISPUTE_NOTICE}
    >
      <ExclamationTriangle className="DisputeNotice__icon" size={20} aria-hidden />
      <SDSFlexbox direction="column" gap="text-medium" alignItems="flex-start">
        <SDSText type="body-large-bold" text={copy.title} />
        <SDSText type="body-medium" color="text-secondary" text={copy.body} />
        <SDSButton
          className="DisputeNotice__action"
          text="Contact support"
          mode="ghost"
          size="small"
          onClick={() => {
            setIsContactingSupport(true);
          }}
          dataTestId={DataTestIds.BILLING.DISPUTE_CONTACT_SUPPORT}
        />
      </SDSFlexbox>
      {isContactingSupport ? (
        <ContactSupportDialog
          isOpen
          topic={ContactTopic.BILLING_AND_PAYMENTS}
          pagePath={ROUTES.BILLING}
          initialMessage={`I have a question about the disputed payment on my ${productTitle} plan.`}
          onClose={() => {
            setIsContactingSupport(false);
          }}
        />
      ) : null}
    </SDSFlexbox>
  );
}
